// frontend/src/components/Charts/RankedDimBarChart.tsx
import { useState } from 'react'
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  ReferenceLine,
} from 'recharts'
import { Box, Typography, Button } from '@mui/material'
import { formatAxisTick, formatCompactNumber } from '~/util/numbers'
import { renderHorizontalBarValueLabel } from './chrome/barValueLabel'
import { MESSAGES } from './chrome/chartChrome'
import { AXIS_TICK, GRID_STROKE, TOOLTIP_NUM_STYLE, ZERO_LINE_STROKE } from './chrome/chartStyle'
import type { RankedDimRow } from './toRankedDimRows'

interface Props {
  title: string
  rows: RankedDimRow[]
  colorForKey: (key: string) => string
  labelForKey: (key: string) => string
  topN?: number
  /** Shared [min, max] domain (Task 9's sharedDomain) — replaces the old abs-max-only
   *  domainMax so a negative row gets axis room below zero instead of being clipped. */
  domain?: [number, number]
}

const ROW_HEIGHT = 36

/** Grouped CP arm: one horizontal bar per dim-value, sorted desc (rows arrive pre-sorted).
 *  Top-N collapsed with an explicit "+ N more" expander (no silent truncation). No legend —
 *  labels are on the Y axis and color is value-keyed (shared map). */
export function RankedDimBarChart({
  title,
  rows,
  colorForKey,
  labelForKey,
  topN = 15,
  domain,
}: Props) {
  const [expanded, setExpanded] = useState(false)
  if (rows.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="subtitle2">{title}</Typography>
        <Typography color="text.secondary">{MESSAGES.noDataForFilters}</Typography>
      </Box>
    )
  }
  const visible = expanded ? rows : rows.slice(0, topN)
  const data = visible.map((r) => ({ key: r.key, label: labelForKey(r.key), value: r.value }))
  const hidden = rows.length - topN
  // Diverging geometry (Task 10): with an explicit domain, its min tells us whether the axis
  // dips negative; without one (no shared-scale caller), fall back to the visible rows themselves
  // — mirrors AbsoluteBarChart's `(props.yDomain?.[0] ?? 0) < 0` gate, adapted for the no-domain case.
  const showZeroLine = domain != null ? domain[0] < 0 : visible.some((r) => (r.value ?? 0) < 0)
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" align="center">
        {title}
      </Typography>
      <Box sx={{ width: '100%', height: data.length * ROW_HEIGHT + 24 }}>
        <ResponsiveContainer>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 8, left: 8, right: 48, bottom: 8 }}
          >
            <CartesianGrid stroke={GRID_STROKE} horizontal={false} />
            {/* Horizontal bars (layout="vertical"): the value axis is the XAxis, so the zero
                boundary is a VERTICAL reference line (x=0, not y=0). */}
            {showZeroLine ? (
              <ReferenceLine
                x={0}
                stroke={ZERO_LINE_STROKE}
                strokeWidth={2}
                label={{ value: '0', position: 'insideTop', fontSize: 10, fill: ZERO_LINE_STROKE }}
              />
            ) : null}
            <XAxis
              type="number"
              tickFormatter={formatAxisTick}
              domain={domain}
              allowDataOverflow={domain != null}
              tick={AXIS_TICK}
            />
            <YAxis type="category" dataKey="label" width={168} interval={0} tick={AXIS_TICK} />
            <Tooltip
              formatter={(v) => (typeof v === 'number' ? formatCompactNumber(v) : String(v))}
              contentStyle={TOOLTIP_NUM_STYLE}
            />
            <Bar dataKey="value" isAnimationActive={false}>
              {data.map((d) => (
                <Cell key={d.key} fill={colorForKey(d.key)} />
              ))}
              <LabelList
                dataKey="value"
                content={(p) =>
                  renderHorizontalBarValueLabel(
                    p as Parameters<typeof renderHorizontalBarValueLabel>[0],
                    formatCompactNumber,
                  )
                }
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
      {rows.length > topN ? (
        <Box sx={{ textAlign: 'center' }}>
          <Button size="small" onClick={() => setExpanded((e) => !e)}>
            {expanded ? MESSAGES.showFewer : MESSAGES.showNMore(hidden)}
          </Button>
        </Box>
      ) : null}
    </Box>
  )
}
